import React, { useState, useEffect } from "react";
import styled from "styled-components";
import {
  MdPhoto, MdVideocam, MdLocationOn,
  MdThumbUp, MdComment, 
  MdVerified,
  MdBusinessCenter,
  MdRepeat,
  MdSend,
  MdVolumeUp, 
  MdVolumeOff,
  MdPsychology
} from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { db, storage, auth } from "./firebase";
import {
  collection, addDoc, getDocs, getDoc,
  doc, query, orderBy, serverTimestamp, where,
  updateDoc, increment, arrayUnion, arrayRemove, setDoc, deleteDoc
} from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { onAuthStateChanged } from "firebase/auth";
import AppHeader from "./AppHeader";

// Rich Text Editor Imports
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import Feedback from "./feedback"; 

// ---------- Method 1: Official Google Generative AI Import ----------
import { GoogleGenerativeAI } from "@google/generative-ai";

// ---------- Local Video Imports (FIXED SYNTAX ERROR HERE) ----------
import Video1 from "./ad1.mp4";
import Video2 from "./ad2.mp4";

const adVideosList = [Video1, Video2];

// ---------- Utility Functions ----------
const youtubeRegex = /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:[^\/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*?[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})(?:[^\s<]*)/g;

const getYouTubeId = (text) => {
    if (!text) return null;
    const tempRegex = /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:[^\/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*?[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
    const match = text.match(tempRegex);
    return match ? match[1] : null;
};

const removeYouTubeLink = (text) => {
  if (!text) return "";
  let cleaned = text.replace(youtubeRegex, "");
  cleaned = cleaned.replace(/<a[^>]*><\/a>/g, "");
  cleaned = cleaned.replace(/\?si=[a-zA-Z0-9_-]*/g, "");
  return cleaned.trim();
};

const formatDate = (isoString) => {
  if (!isoString) return "";
  const date = new Date(isoString);
  return date.toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', 
      hour: 'numeric', minute: '2-digit', hour12: true 
  });
};

// ---------- Styled Components ----------

const Container = styled.div` 
  background-color: #f5f7fa; 
  min-height: 100vh; 
  display: flex; 
  justify-content: center; 
  padding: 8px; 
  box-sizing: border-box;
  @media (min-width: 768px) { padding: 20px; }
`;

const MainLayout = styled.div` 
  display: flex;
  flex-direction: column;
  width: 100%; 
  max-width: 1200px; 
  gap: 15px;
  @media (min-width: 1024px) {
    display: grid;
    grid-template-columns: 270px 1fr 270px;
    align-items: start;
  }
  @media (min-width: 768px) and (max-width: 1023px) {
    display: grid;
    grid-template-columns: 240px 1fr;
  }
`;

const Sidebar = styled.div` 
  background: white; border-radius: 12px; box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08); 
  padding: 15px; text-align: center; height: fit-content;
  @media (min-width: 768px) { position: sticky; top: 20px; } 
`;

const RightSidebar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  @media (max-width: 1023px) { 
    display: flex; 
    order: 3; 
  } 
  @media (min-width: 1024px) {
    position: sticky;
    top: 20px;
  }
`;

const TrendingCard = styled.div`
  background: white; border-radius: 12px; box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  padding: 20px;
  h3 { display: flex; align-items: center; gap: 8px; font-size: 1.1rem; margin-bottom: 15px; color: #1a202c; }
`;

const AdCardFull = styled.div`
  background: white; border-radius: 12px; box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  padding: 12px; margin-bottom: 15px; position: relative;
`;

const VideoWrapper = styled.div`
  width: 100%; height: 180px; border-radius: 10px; overflow: hidden; position: relative; background: #000;
`;

const FullVideo = styled.video`
  width: 100%; height: 100%; object-fit: cover;
`;

const SpeakerButton = styled.div`
  position: absolute; top: 8px; right: 8px; background: rgba(0, 0, 0, 0.5);
  color: white; width: 28px; height: 28px; border-radius: 50%; display: flex;
  align-items: center; justify-content: center; cursor: pointer; z-index: 10;
`;

const HashtagList = styled.div`
  display: flex; flex-direction: column; gap: 12px;
  span { color: #1a73e8; font-weight: 500; cursor: pointer; font-size: 13px; &:hover { text-decoration: underline; } }
`;

const FooterLinksCard = styled.div`
  background: white; border-radius: 8px; box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  padding: 12px; font-size: 11px; color: #5f6368; text-align: center;
  div { display: flex; justify-content: center; gap: 8px; flex-wrap: wrap; margin-bottom: 5px; }
  span { cursor: pointer; &:hover { color: #1a73e8; } }
`;

const CoverImage = styled.div` 
  height: 60px; background-color: #1a73e8; border-radius: 10px; 
  background-image: url(${(props) => props.$src || ""}); background-size: cover; background-position: center;
  @media (min-width: 768px) { height: 80px; }
`;

const ProfileImage = styled.div` 
  width: 60px; height: 60px; border-radius: 50%; overflow: hidden; 
  margin: -30px auto 10px; border: 3px solid white; 
  img { width: 100%; height: 100%; object-fit: cover; } 
  @media (min-width: 768px) { width: 70px; height: 70px; margin: -35px auto 10px; }
`;

const SidebarNameWrapper = styled.div` display: flex; align-items: center; justify-content: center; gap: 5px; margin: 5px 0; `;
const SidebarName = styled.h3` font-size: 0.95rem; margin: 0; color: #1a202c; font-weight: 700; `;
const VerifiedIcon = styled(MdVerified)` color: #1d99f0; font-size: 1rem; `;

const BusinessButton = styled.button`
  width: 100%; padding: 10px; margin-top: 10px; border: none; border-radius: 8px;
  background: #f5f7fa; color: #01070fff; font-weight: 700; cursor: pointer;
  display: flex; align-items: center; justify-content: center; gap: 6px; font-size: 12px;
  &:disabled { background: #e0e0e0; color: #9e9e9e; cursor: not-allowed; }
`;

const ManageBusinessButton = styled(BusinessButton)`
  background: #1a73e8; color: white; margin-top: 6px;
  &:hover { background: #1557b0; }
`;

const FeedSection = styled.div` 
  display: flex; 
  flex-direction: column; 
  width: 100%; 
  max-width: 100vw; 
  overflow-x: hidden;
  @media (max-width: 1023px) {
    order: 2; 
  }
`;

const PostCard = styled.div` 
  background: white; 
  border-radius: 12px; 
  padding: 12px; 
  margin-bottom: 12px; 
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  width: 100%;
  box-sizing: border-box; 
  overflow: hidden;
`;

const EditorWrapper = styled.div`
  margin-bottom: 10px;
  .ql-container { min-height: 80px; font-size: 14px; }
  .ql-toolbar { background: #f8f9fa; }
`;

const RichPostText = styled.div`
  font-size: 14px; 
  line-height: 1.6; 
  color: #333; 
  margin: 10px 0;
  white-space: pre-wrap; 
  word-wrap: break-word; 
  overflow-wrap: break-word;
  word-break: break-word; 
  
  p { margin-bottom: 8px; }
  img { max-width: 100%; height: auto; }
`;

const VideoContainer = styled.div`
  position: relative; padding-bottom: 56.25%; height: 0; overflow: hidden; border-radius: 8px; margin-top: 10px;
  iframe, video { position: absolute; top: 0; left: 0; width: 100%; height: 100%; border: 0; }
`;

const PostActions = styled.div` 
  display: flex; justify-content: space-between; margin-top: 10px; align-items: center; gap: 5px;
  @media (max-width: 400px) { flex-wrap: wrap; }
`;

const PostButton = styled.button` 
  background-color: #1a73e8; color: white; border: none; border-radius: 8px; padding: 8px 16px; font-weight: 600; cursor: pointer; font-size: 13px;
  &:disabled { background-color: #9e9e9e; }
`;

const AIButton = styled.button`
  background: linear-gradient(135deg, #6200ea 0%, #1a73e8 100%);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 8px 14px;
  font-weight: 600;
  cursor: pointer;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 6px;
  box-shadow: 0 2px 4px rgba(26, 115, 232, 0.2);
  transition: all 0.2s ease;
  &:hover {
    opacity: 0.9;
    transform: translateY(-1px);
  }
  &:disabled {
    background: #e0e0e0;
    color: #9e9e9e;
    cursor: not-allowed;
    box-shadow: none;
    transform: none;
  }
`;

const PostDisplay = styled(PostCard)` 
  position: relative; 
  display: flex;
  flex-direction: column;
`;

const PostHeaderWrapper = styled.div` 
  display: flex; 
  align-items: center; 
  margin-bottom: 10px; 
  width: 100%;
`;

const PostAvatarSmall = styled.div` 
  width: 40px; 
  height: 40px; 
  min-width: 40px; 
  border-radius: 50%; 
  overflow: hidden; 
  margin-right: 10px; 
  border: 1px solid #eee; 
  cursor: pointer;
  img { width: 100%; height: 100%; object-fit: cover; } 
`;

const PostActionBar = styled.div` 
  display: flex; justify-content: space-between; padding-top: 10px; border-top: 1px solid #eee; margin-top: 10px; 
`;

const PostActionButton = styled.button` 
  background: none; border: none; color: ${(props) => (props.$active ? "#1a73e8" : "#5f6368")}; 
  padding: 8px 2px; cursor: pointer; display: flex; align-items: center; gap: 4px; font-weight: 600; font-size: 12px;
`;

const RepostBadge = styled.div` font-size: 10px; color: #5f6368; display: flex; align-items: center; gap: 4px; margin-bottom: 6px; `;

const CommentSection = styled.div` margin-top: 10px; padding-top: 10px; border-top: 1px solid #f0f2f5; `;

const CommentInputWrapper = styled.div` display: flex; align-items: center; gap: 8px; margin-bottom: 10px; `;

const CommentInput = styled.input` 
  flex: 1; 
  background-color: #f0f2f5; 
  border: none; 
  border-radius: 20px; 
  padding: 8px 14px; 
  outline: none; 
  font-size: 13px; 
  width: 100%;
`;

const CommentList = styled.div` display: flex; flex-direction: column; gap: 8px; `;

const CommentItem = styled.div` display: flex; gap: 8px; `;

const CommentBubble = styled.div` 
  background-color: #f0f2f5; 
  border-radius: 12px; 
  padding: 8px 12px; 
  max-width: 85%;
  word-break: break-word;
`;

const CommentAuthor = styled.h5` margin: 0; font-size: 12px; font-weight: 700; cursor: pointer; &:hover{text-decoration: underline;} `;
const CommentText = styled.p` margin: 2px 0 0; font-size: 13px; color: #333; `;

const Home = () => {
  const navigate = useNavigate();
  const [postText, setPostText] = useState("");
  const [file, setFile] = useState(null);
  const [fileType, setFileType] = useState(null);
  const [posts, setPosts] = useState([]);
  const [user, setUser] = useState(null);
  const [profileData, setProfileData] = useState(null);
  const [authorProfiles, setAuthorProfiles] = useState({});
  const [isReviewing, setIsReviewing] = useState(false);
  const [previewMediaUrl, setPreviewMediaUrl] = useState(null);
  const [isMediaUploading, setIsMediaUploading] = useState(false);
  const [showCommentsForPost, setShowCommentsForPost] = useState(null);
  const [postComments, setPostComments] = useState({});
  const [commentTexts, setCommentTexts] = useState({}); 
  const [businessProfilesCount, setBusinessProfilesCount] = useState(0);
  const [userBusinessName, setUserBusinessName] = useState(""); 
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const [currentAdIndex, setCurrentAdIndex] = useState(0);
  const [isMuted, setIsMuted] = useState(true);
  const [aiLoading, setAiLoading] = useState(false);

  const handleNextAd = () => {
    setCurrentAdIndex((prev) => (prev + 1) % adVideosList.length);
  };

  const quillModules = {
    toolbar: [
      ['bold', 'italic', 'underline'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      ['clean']
    ],
  };

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        const emailId = currentUser.email;

        let profileRef = doc(db, "profiles", emailId);
        let profileSnap = await getDoc(profileRef);

        if (profileSnap.exists()) {
          setProfileData(profileSnap.data());
        } else {
          const oldLogicId = emailId.split('@')[0].replace(/[^a-zA-Z0-9]/g, '');
          let oldProfileRef = doc(db, "profiles", oldLogicId);
          let oldProfileSnap = await getDoc(oldProfileRef);

          if (oldProfileSnap.exists()) {
            const data = oldProfileSnap.data();
            setProfileData(data);
          }
        }

        try {
          const bizQuery = query(
              collection(db, "businessPages"), 
              where("ownerUid", "==", currentUser.uid)
          );
          const bizQuerySnap = await getDocs(bizQuery);
          setBusinessProfilesCount(bizQuerySnap.size);
          
          if (!bizQuerySnap.empty) {
              const firstBizDoc = bizQuerySnap.docs[0].data();
              setUserBusinessName(firstBizDoc.businessName || "");
          }
        } catch (error) {
            console.error("Error fetching business pages count:", error);
        }

        fetchPosts();
      } else {
        navigate("/login");
      }
    });
    return () => unsub();
  }, [navigate]);

  const fetchPosts = async () => {
    const qPosts = query(collection(db, "posts"), orderBy("createdAt", "desc"));
    const snap = await getDocs(qPosts);
    const firePosts = snap.docs.map(d => ({ 
      id: d.id, sourceCollection: "posts", ...d.data(), 
      createdAt: d.data().createdAt?.toDate() || new Date()
    }));

    const qBizPosts = query(collection(db, "businessPosts"), orderBy("createdAt", "desc"));
    const bizSnap = await getDocs(qBizPosts); // FIXED: Changed from getDocs(bizSnap) to getDocs(qBizPosts)
    const fireBizPosts = bizSnap.docs.map(d => {
      const data = d.data();
      return {
        id: d.id, sourceCollection: "businessPosts", ...data,
        userName: data.businessName || "Business",
        profileImageUrl: data.businessLogo || "/default-avatar.png",
        createdAt: data.createdAt?.toDate() || new Date()
      };
    });

    const combined = [...firePosts, ...fireBizPosts].sort((a, b) => b.createdAt - a.createdAt);
    const finalPosts = combined.map(p => ({ ...p, createdAt: p.createdAt.toISOString() }));
    setPosts(finalPosts);
    fetchPostAuthors(finalPosts);
  };

  const fetchPostAuthors = async (currentPosts) => {
    const profiles = { ...authorProfiles };
    for (const p of currentPosts) {
        if (p.sourceCollection === "posts" && !profiles[p.userId]) {
            const d = await getDocs(query(collection(db, "profiles"), where("uid", "==", p.userId)));
            if (!d.empty) profiles[p.userId] = d.docs[0].data();
        }
    }
    setAuthorProfiles(profiles);
  };

  const handleLike = async (post) => {
    const collName = post.sourceCollection || "posts";
    const postRef = doc(db, collName, post.id);
    const isLiked = post.likedBy?.includes(user.uid);
    await updateDoc(postRef, {
      likes: increment(isLiked ? -1 : 1),
      likedBy: isLiked ? arrayRemove(user.uid) : arrayUnion(user.uid)
    });
    fetchPosts();
  };

  const handleRepost = async (post) => {
    if (!window.confirm("Do you want to repost this?")) return;
    try {
      await addDoc(collection(db, "posts"), {
        text: post.text || "", mediaUrl: post.mediaUrl || null, mediaType: post.mediaType || "image",
        userId: user.uid, userName: profileData?.fullName || "User", profileImageUrl: profileData?.profileImageUrl,
        isRepost: true, originalAuthor: post.userName, likes: 0, comments: 0, reposts: 0, likedBy: [], createdAt: serverTimestamp()
      });
      await updateDoc(doc(db, post.sourceCollection || "posts", post.id), { reposts: increment(1) });
      alert("Reposted!"); fetchPosts();
    } catch (error) { console.error(error); }
  };

  const toggleComments = (post) => {
    if (showCommentsForPost === post.id) { setShowCommentsForPost(null); } 
    else { setShowCommentsForPost(post.id); fetchComments(post); }
  };

  const fetchComments = async (post) => {
    const q = query(collection(db, post.sourceCollection || "posts", post.id, "comments"), orderBy("createdAt", "asc"));
    const snap = await getDocs(q);
    setPostComments(prev => ({ ...prev, [post.id]: snap.docs.map(d => ({ id: d.id, ...d.data() })) }));
  };

  const handleAddComment = async (post) => {
    const text = commentTexts[post.id];
    if (!text?.trim()) return;
    try {
      await addDoc(collection(db, post.sourceCollection || "posts", post.id, "comments"), {
        text: text.trim(), userId: user.uid, userName: profileData?.fullName || "User",
        profileImageUrl: profileData?.profileImageUrl, createdAt: serverTimestamp()
      });
      setCommentTexts(prev => ({ ...prev, [post.id]: "" })); fetchComments(post);
    } catch (error) { console.error(error); }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setFileType(selectedFile.type.startsWith("video") ? "video" : "image");
    }
  };

  const handleReview = async () => {
    if (file) {
      setIsMediaUploading(true);
      const storageRef = ref(storage, `posts/${Date.now()}_${file.name}`);
      const uploadTask = uploadBytesResumable(storageRef, file);
      uploadTask.on("state_changed", null, (err) => { setIsMediaUploading(false); }, 
        async () => {
          const url = await getDownloadURL(uploadTask.snapshot.ref);
          setPreviewMediaUrl(url); setIsMediaUploading(false); setIsReviewing(true);
        }
      );
    } else { setIsReviewing(true); }
  };

  const handleUpload = async () => {
    try {
        await addDoc(collection(db, "posts"), {
          text: postText, mediaUrl: previewMediaUrl || null, mediaType: fileType || "image",
          userId: user.uid, userName: profileData?.fullName || "User", profileImageUrl: profileData?.profileImageUrl,
          likes: 0, comments: 0, reposts: 0, likedBy: [], createdAt: serverTimestamp()
        });
        setPostText(""); setFile(null); setPreviewMediaUrl(null); setIsReviewing(false); fetchPosts();
    } catch (error) { console.error(error); }
  };

  // ---------- METHOD 1 INTEGRATED VIA OFFICIAL SDK LIBRARY ----------
  const handleGenerateAI = async () => {
    const plainText = postText.replace(/<[^>]*>/g, '').trim();
    if (!plainText) {
      alert("Please enter some text first to rewrite with AI!");
      return;
    }

    setAiLoading(true);
    try {
      const apiKey = "AIzaSyDb_axep4Wwy0NpbX2cIQ61Y3o6RFBUrpo"; 
      
      // Official SDK Initialization
      const ai = new GoogleGenerativeAI(apiKey);
      const model = ai.getGenerativeModel({ model: "gemini-1.5-flash" });

      const prompt = `Rewrite and professionalize the following post content for a professional student and educators network platform. Maintain the original core context, but make it engaging and clear. Output ONLY the rewritten text without markdown, quotes, or conversational introductions:\n\n${plainText}`;

      // SDK Method integration (Bypasses local proxy/router endpoint parsing blocks)
      const result = await model.generateContent({
        contents: [{ role: "user", parts: [{ text: prompt }] }],
      });

      if (result.response && typeof result.response.text === 'function') {
        const responseText = result.response.text();
        setPostText(`<p>${responseText.trim()}</p>`);
      } else {
        alert("Failed to rewrite content using AI. Please try again.");
      }
    } catch (error) {
      console.error("AI Generation Error via Official SDK:", error);
      alert("An error occurred while connecting to AI service.");
    } finally {
      setAiLoading(false);
    }
  };

  const handleProfileClick = (userId, source, userName) => {
    if (source === "businessPosts") {
      navigate(`/business-view/${userName.replace(/\s+/g, '')}`);
    } else {
      navigate(`/profile/${userName}`);
    }
  };

  const handleCreateBusiness = () => {
    if (businessProfilesCount >= 10) {
      alert("You have reached the maximum limit of 10 Business Profiles.");
    } else {
      navigate("/CreateBussinessPage");
    }
  };

  const handleManageBusiness = () => {
    if (userBusinessName) {
      navigate(`/business/${userBusinessName.replace(/\s+/g, '')}`);
    } else {
      alert("No active business profile found to manage.");
    }
  };

  const isEligibleForBusiness = () => {
    const role = profileData?.mainRole?.toLowerCase() || "";
    return role.includes('business') || role.includes('owner') || role.includes('manager');
  };

  return (
    <>
      <AppHeader profileImageUrl={profileData?.profileImageUrl} initials={profileData?.fullName?.[0]} />
      <Container>
        <MainLayout>
          {/* Left Sidebar */}
          <Sidebar>
            <CoverImage $src={profileData?.coverImageUrl} />
            <ProfileImage>
              <img 
                src={profileData?.profileImageUrl || "/default-avatar.png"} 
                alt="User" 
                style={{cursor: 'pointer'}}
                onClick={() => {
                  const formattedName = profileData?.fullName ? encodeURIComponent(profileData.fullName.replace(/\s+/g, '')) : "";
                  navigate(`/profile/${formattedName}`);
                }}
                onError={(e) => {e.target.src = "/default-avatar.png"}}
              />
            </ProfileImage>
            <SidebarNameWrapper>
              <SidebarName 
                style={{cursor: 'pointer'}} 
                onClick={() => navigate(`/profile/${user?.uid}`)}
              >
                {profileData?.fullName || "Loading..."}
              </SidebarName>
              {isEligibleForBusiness() && <VerifiedIcon />}
            </SidebarNameWrapper>
            <p style={{fontSize: '12px', color: '#5f6368', margin: '5px 0'}}><MdLocationOn /> {profileData?.city || "Tamil Nadu"}</p>
            <button style={{width:'100%', padding:'10px', marginTop:'8px', border:'none', borderRadius:'8px', background: '#f1f3f4', fontWeight:'700', cursor:'pointer', fontSize: '12px'}} onClick={() => navigate("/my-connections")}>⭐ My Connections</button>
            
            {isEligibleForBusiness() && (
              <>
                <BusinessButton 
                  onClick={handleCreateBusiness}
                  disabled={businessProfilesCount >= 10}
                >
                  <MdBusinessCenter size={16} /> 
                  {businessProfilesCount >= 10 
                    ? "Create Business Profile (Limit Reached 10)" 
                    : `Create Business Profile (${businessProfilesCount}/10)`}
                </BusinessButton>

                {businessProfilesCount > 0 && (
                  <ManageBusinessButton onClick={handleManageBusiness}>
                    <MdBusinessCenter size={16} /> 
                    Manage Business Profiles
                  </ManageBusinessButton>
                )}
              </>
            )}
          </Sidebar>

          {/* Feed Section */}
          <FeedSection>
            {!isReviewing ? (
              <PostCard>
                <EditorWrapper>
                  <ReactQuill theme="snow" value={postText} onChange={setPostText} modules={quillModules} placeholder="What's on your mind?" />
                </EditorWrapper>
                <PostActions>
                  <div style={{display:'flex', gap:'15px'}}>
                    <label style={{cursor:'pointer', fontSize:'13px', display:'flex', alignItems:'center', gap:'5px', fontWeight:'500'}}><MdPhoto color="#1a73e8" size={20}/> Photo <input type="file" hidden accept="image/*" onChange={handleFileChange}/></label>
                    <label style={{cursor:'pointer', fontSize:'13px', display:'flex', alignItems:'center', gap:'5px', fontWeight:'500'}}><MdVideocam color="#1a73e8" size={20}/> Video <input type="file" hidden accept="video/*" onChange={handleFileChange}/></label>
                  </div>
                  
                  <div style={{display: 'flex', gap: '10px'}}>
                    <AIButton onClick={handleGenerateAI} disabled={aiLoading || !postText.replace(/<[^>]*>/g, '').trim()}>
                      <MdPsychology size={18} />
                      {aiLoading ? "Rewriting..." : "Generate with AI"}
                    </AIButton>
                    
                    <PostButton onClick={handleReview} disabled={isMediaUploading || (!postText.replace(/<[^>]*>/g, '').trim() && !file)}>{isMediaUploading ? "..." : "Review"}</PostButton>
                  </div>
                </PostActions>
              </PostCard>
            ) : (
              <PostCard>
                <h4 style={{fontSize: '14px', marginBottom:'10px'}}>Review Post</h4>
                <RichPostText dangerouslySetInnerHTML={{ __html: removeYouTubeLink(postText) }} />
                {previewMediaUrl && (fileType === "video" ? <VideoContainer><video src={previewMediaUrl} controls/></VideoContainer> : <img src={previewMediaUrl} style={{width:'100%', borderRadius:'8px', marginTop:'10px'}} alt="preview"/>)}
                <div style={{display: 'flex', gap: '10px', marginTop: '15px'}}><PostButton onClick={handleUpload}>Post Now</PostButton><PostButton style={{background: '#eee', color: '#333'}} onClick={() => setIsReviewing(false)}>Edit</PostButton></div>
              </PostCard>
            )}

            {posts.map(post => {
              const ytId = getYouTubeId(post.text);
              const cleanCaption = removeYouTubeLink(post.text);
              const isBiz = post.sourceCollection === "businessPosts";
              const authorId = post.userId;
              const authorName = isBiz ? post.userName : (authorProfiles[post.userId]?.fullName || post.userName);
              const authorImg = isBiz ? post.profileImageUrl : (authorProfiles[post.userId]?.profileImageUrl || post.profileImageUrl || "/default-avatar.png");
              
              return (
                <PostDisplay key={post.id}>
                  {post.isRepost && <RepostBadge><MdRepeat size={12} /> Reposted from <strong>{post.originalAuthor}</strong></RepostBadge>}
                  {isBiz && <div style={{fontSize: '9px', color: '#1a73e8', fontWeight: 'bold', marginBottom: '8px', letterSpacing:'0.5px'}}>SPONSORED / BUSINESS</div>}
                  <PostHeaderWrapper>
                    <PostAvatarSmall onClick={() => handleProfileClick(authorId, post.sourceCollection, post.userName)}>
                        <img src={authorImg} alt="u" />
                    </PostAvatarSmall>
                    <div>
                      <h4 
                        style={{margin:0, fontSize:'14px', fontWeight:'700', cursor: 'pointer'}} 
                        onClick={() => handleProfileClick(authorId, post.sourceCollection, post.userName)}
                      >
                        {authorName}
                      </h4>
                      <span style={{fontSize:'11px', color: '#888'}}>{formatDate(post.createdAt)}</span>
                    </div>
                  </PostHeaderWrapper>
                  
                  <RichPostText dangerouslySetInnerHTML={{ __html: cleanCaption }} />
                  
                  {ytId && (
                    <VideoContainer>
                      <iframe 
                        src={`https://www.youtube.com/embed/${ytId}`} 
                        title="YouTube Video" 
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                        allowFullScreen 
                      />
                    </VideoContainer>
                  )}

                  {post.mediaUrl && (post.mediaType === "video" ? <VideoContainer><video src={post.mediaUrl} controls /></VideoContainer> : <img src={post.mediaUrl} style={{width:'100%', borderRadius:'8px', marginTop:'8px'}} alt="post" />)}

                  <PostActionBar>
                    <PostActionButton onClick={() => handleLike(post)} $active={post.likedBy?.includes(user?.uid)}><MdThumbUp size={18}/> {post.likes || 0}</PostActionButton>
                    <PostActionButton onClick={() => toggleComments(post)}><MdComment size={18}/> Comment</PostActionButton>
                    <PostActionButton onClick={() => handleRepost(post)}><MdRepeat size={18}/> {post.reposts || 0}</PostActionButton>
                  </PostActionBar>

                  {showCommentsForPost === post.id && (
                    <CommentSection>
                      <CommentInputWrapper>
                        <CommentInput placeholder="Write a comment..." value={commentTexts[post.id] || ""} onChange={(e) => setCommentTexts({...commentTexts, [post.id]: e.target.value})} onKeyPress={(e) => e.key === 'Enter' && handleAddComment(post)}/>
                        <MdSend size={20} color="#1a73e8" style={{cursor:'pointer'}} onClick={() => handleAddComment(post)}/>
                      </CommentInputWrapper>
                      <CommentList>
                        {postComments[post.id]?.map((comment) => (
                          <CommentItem key={comment.id}>
                            <PostAvatarSmall 
                                style={{width: '32px', height: '32px'}}
                                onClick={() => navigate(`/profile/${comment.userId}`)}
                            >
                                <img src={comment.profileImageUrl || "/default-avatar.png"} alt="c" />
                            </PostAvatarSmall>
                            <CommentBubble>
                                <CommentAuthor onClick={() => navigate(`/profile/${comment.userId}`)}>{comment.userName}</CommentAuthor>
                                <CommentText>{comment.text}</CommentText>
                            </CommentBubble>
                          </CommentItem>
                        ))}
                      </CommentList>
                    </CommentSection>
                  )}
                </PostDisplay>
              );
            })}
          </FeedSection>

          {/* Right Sidebar */}
          <RightSidebar>
            <AdCardFull>
              <h3 style={{ fontSize: '11px', color: '#888', textTransform: 'uppercase', marginBottom: '10px' }}>Advertisement</h3>
              <VideoWrapper>
                <SpeakerButton onClick={() => setIsMuted(!isMuted)}>
                  {isMuted ? <MdVolumeOff size={18} /> : <MdVolumeUp size={18} />}
                </SpeakerButton>
                <FullVideo 
                  key={adVideosList[currentAdIndex]} 
                  src={adVideosList[currentAdIndex]} 
                  autoPlay 
                  muted={isMuted}
                  onEnded={handleNextAd} 
                  playsInline
                />
              </VideoWrapper>
              <div style={{ marginTop: '10px' }}>
                <h4 style={{ fontSize: '13px', margin: '0', color: '#1a202c' }}>Build Your Professional Network</h4>
                <p style={{ fontSize: '11px', color: '#5f6368', margin: '4px 0 0' }}>Step into Success with a 25% discount.</p>
              </div>
            </AdCardFull>

            <TrendingCard>
              <h3>📈 Trending Now</h3>
              <HashtagList>
                <span>#Learn about Business Strategy</span>
                <span>#Business Updates</span>
                <span>#Current Trends</span>
                <span>#AI in Marketing</span>
                <span>#Current growth</span>
                <span>#America Business</span>
                <span>#Current Affairs</span>
              </HashtagList>
            </TrendingCard>

            <FooterLinksCard>
             <div>
                <span onClick={() => navigate("/privacypolicy")}>Privacy Policy</span> 
                | <span onClick={() => navigate("/userguideline")}>User Guide</span>  
                | <span onClick={() => setIsFeedbackOpen(true)}>Feedback</span>
              </div>
            </FooterLinksCard>

            <FooterLinksCard>
              <div>
                <span onClick={() => navigate("/cancellation")}>Refund</span>  
                | <span onClick={() => navigate("/contactus")}>Contact</span>
              </div>
            </FooterLinksCard>
          </RightSidebar>
        </MainLayout>
      </Container>
      
      <Feedback 
        isOpen={isFeedbackOpen} 
        onClose={() => setIsFeedbackOpen(false)} 
        userProfile={profileData}
      />
    </>
  );
};

export default Home;